import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCcw, GitCompare, Loader2 } from 'lucide-react';
import * as api from '@/api/client';
import type { Stage1SummaryField, Stage1Snapshot } from '@/api/client';
import DiffModal, { type DiffFieldMap } from './DiffModal';
import { isLostRequest, waitForChange } from './reextract';

import { apiErrorText } from '../../../lib/describeApiError';
/** Field mapping — the four editable text fields (not prior art).
 *  `apiField` is what the PATCH endpoint expects; `contentKey` is
 *  where the value lives inside ``stage_state.content.summary``. */
const TEXT_FIELDS: Array<{
  apiField: Exclude<Stage1SummaryField, 'prior_art'>;
  contentKey: 'overview' | 'problem' | 'solution' | 'implementation';
  label: string;
  accent?: boolean;
}> = [
  { apiField: 'overall',        contentKey: 'overview',       label: 'Overall Invention', accent: true },
  { apiField: 'problem',        contentKey: 'problem',        label: 'Problem' },
  { apiField: 'solution',       contentKey: 'solution',       label: 'Solution' },
  { apiField: 'implementation', contentKey: 'implementation', label: 'Implementation' },
];

/** Decode `idf_edited_fields` which arrives as a Python list from
 *  Postgres (TEXT[] → array) and as a JSON string from SQLite (TEXT).
 *  Returns a normalised string array either way. Exported so the
 *  Stage 1 freeze bar can show "{N} of 5 fields edited from AI
 *  defaults" without re-implementing the same dialect handling. */
export function decodeEditedFields(raw: unknown): Stage1SummaryField[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((v) => typeof v === 'string') as Stage1SummaryField[];
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) ? (parsed.filter((v) => typeof v === 'string') as Stage1SummaryField[]) : [];
    } catch { return []; }
  }
  return [];
}

interface SummaryTabProps {
  matterId: string;
  /** `content.summary` blob from stage_state.  Pulled as `any` because
   *  the global MatterFullState types don't describe it; runtime shape
   *  is `{overview, problem, solution, implementation, prior_art_mentioned[]}`. */
  summary: any;
  /** Raw `idf_edited_fields` column value (list on Postgres, JSON
   *  string on SQLite). */
  editedFieldsRaw: unknown;
  isFrozen: boolean;
  /** Refetch the matter so we pick up the new content/edited_fields
   *  after a PATCH or re-extract. */
  onRefresh: () => Promise<void> | void;
  onError: (msg: string | null) => void;
  /** Overall completeness score to render inline with the header
   *  buttons. null when no scores yet (pre-Analyze IDF). Parent passes
   *  this in — the old absolutely-positioned callout in Stage1.tsx
   *  used to overlap the tab-local buttons below, so it lives here
   *  now. */
  overallScore?: number | null;
  potentialFromQa?: { questionCount: number; potential: number } | null;
  onJumpToQa?: () => void;
}

/**
 * Summary tab — the five AI-extracted field cards.
 *
 * On mount we hit GET /stage1/snapshot once so the backend persists an
 * `idf_ai_snapshot` for legacy matters extracted before the column
 * existed. The response is idempotent after the first call; we don't
 * need to consume it here (the Diff-vs-AI modal reads it in a later
 * step), we only need the side effect.
 */
export default function SummaryTab({
  matterId,
  summary,
  editedFieldsRaw,
  isFrozen,
  onRefresh,
  onError,
  overallScore,
  potentialFromQa,
  onJumpToQa,
}: SummaryTabProps) {
  const editedFields = decodeEditedFields(editedFieldsRaw);

  // Snapshot kept in local state so the Diff-vs-AI modal has data to
  // render even on freshly-extracted matters whose `idf_ai_snapshot`
  // was lazily created by this same fetch. The GET endpoint is
  // idempotent on the server: subsequent calls return the stored
  // snapshot without overwriting it. Failures are silent — the modal
  // shows a "no snapshot yet" hint in that case.
  const [snapshot, setSnapshot] = useState<Stage1Snapshot | null>(null);
  const snapshotRequested = useRef<string | null>(null);
  useEffect(() => {
    if (!matterId || snapshotRequested.current === matterId) return;
    snapshotRequested.current = matterId;
    let cancelled = false;
    api.getStage1Snapshot(matterId)
      .then((res) => { if (!cancelled) setSnapshot(res.snapshot); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [matterId]);

  // ── Diff-vs-AI modal state ────────────────────────────────────
  const [isDiffOpen, setIsDiffOpen] = useState(false);
  const currentFieldsForDiff: DiffFieldMap = {
    overall:        typeof summary?.overview === 'string' ? summary.overview : '',
    problem:        typeof summary?.problem === 'string' ? summary.problem : '',
    solution:       typeof summary?.solution === 'string' ? summary.solution : '',
    implementation: typeof summary?.implementation === 'string' ? summary.implementation : '',
    prior_art:      Array.isArray(summary?.prior_art_mentioned) ? summary.prior_art_mentioned : [],
  };
  const handleRevert = useCallback(async (
    field: Stage1SummaryField, value: string | string[],
  ) => {
    onError(null);
    try {
      await api.patchStage1Summary(matterId, field, value);
      await onRefresh();
    } catch (e: any) {
      onError(apiErrorText(e, `Revert failed (${field})`));
    }
  }, [matterId, onError, onRefresh]);

  // ── Re-extract ────────────────────────────────────────────────
  const [reExtracting, setReExtracting] = useState(false);
  const summaryRef = useRef(summary);
  useEffect(() => { summaryRef.current = summary; }, [summary]);
  const handleReextract = useCallback(async () => {
    if (isFrozen) return;
    const ok = window.confirm(
      'This will replace your edited fields with fresh AI extraction.\n\nContinue?',
    );
    if (!ok) return;
    setReExtracting(true);
    onError(null);
    const before = JSON.stringify(summaryRef.current ?? null);
    try {
      await api.scoreStage1(matterId, { replaceEdits: true });
      await onRefresh();
    } catch (e: any) {
      if (isLostRequest(e)) {
        // The request was cut off in transit; the server is probably still
        // extracting. Wait for the summary to change instead of reporting
        // a failure that has not happened.
        const landed = await waitForChange({
          isChanged: () => JSON.stringify(summaryRef.current ?? null) !== before,
          refresh: onRefresh,
        });
        if (!landed) {
          onError(
            'Re-extract is taking longer than expected. It may still finish — '
            + 'check again in a minute or two.',
          );
        }
      } else {
        onError(apiErrorText(e, 'Re-extract failed'));
      }
    } finally {
      setReExtracting(false);
    }
  }, [isFrozen, matterId, onError, onRefresh]);

  // Per-field regenerate fetches a fresh AI suggestion via the
  // dedicated backend endpoint and renders it in a Suggestion panel
  // below the field card — the drafter Accepts (PATCH writes the
  // value, EDITED badge follows from the diff with idf_ai_snapshot)
  // or Dismisses (panel goes away, no DB write). The full Re-extract
  // path stays available in the header for the "regenerate everything
  // from scratch" workflow.
  const [regenField, setRegenField] = useState<Stage1SummaryField | null>(null);
  const [suggestions, setSuggestions] = useState<
    Partial<Record<Stage1SummaryField, string | string[]>>
  >({});
  const handleRegenerate = useCallback(async (field: Stage1SummaryField) => {
    if (isFrozen) return;
    setRegenField(field);
    onError(null);
    try {
      const res = await api.regenerateStage1SummaryField(matterId, field);
      setSuggestions((s) => ({ ...s, [field]: res.suggestion }));
    } catch (e: any) {
      onError(apiErrorText(e, `Regenerate failed (${field})`));
    } finally {
      setRegenField(null);
    }
  }, [isFrozen, matterId, onError]);

  const handleAcceptSuggestion = useCallback(async (
    field: Stage1SummaryField, value: string | string[],
  ) => {
    onError(null);
    try {
      await api.patchStage1Summary(matterId, field, value);
      await onRefresh();
      setSuggestions((s) => { const c = { ...s }; delete c[field]; return c; });
    } catch (e: any) {
      onError(apiErrorText(e, `Accept failed (${field})`));
    }
  }, [matterId, onError, onRefresh]);

  const handleDismissSuggestion = useCallback((field: Stage1SummaryField) => {
    setSuggestions((s) => { const c = { ...s }; delete c[field]; return c; });
  }, []);

  return (
    <div data-testid="stage-1-summary-tab" className="space-y-4">
      {/* ── Header row ─────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 pb-2 border-b border-zinc-100">
        <div className="min-w-0">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
            AI-Extracted Summary
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            Click any field to edit. Edits become the authoritative version passed to Stage 2.
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {typeof overallScore === 'number' && (
            <div
              data-testid="summary-score-inline"
              className="flex flex-col items-end leading-none text-right"
            >
              <div className="text-xs font-mono text-zinc-700">
                <span className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {Math.round(overallScore)}
                </span>
                <span className="text-zinc-400">/100</span>
              </div>
              {potentialFromQa && onJumpToQa && (
                <button
                  type="button"
                  data-testid="summary-score-jump-qa"
                  onClick={onJumpToQa}
                  className="text-[10px] font-mono uppercase tracking-wider text-[#002FA7] hover:underline mt-0.5 whitespace-nowrap"
                  title={`${potentialFromQa.questionCount} question${potentialFromQa.questionCount === 1 ? '' : 's'} can push this to ${potentialFromQa.potential}`}
                >
                  → Q&amp;A ({potentialFromQa.questionCount})
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            data-testid="summary-diff-vs-ai-button"
            onClick={() => {
              setIsDiffOpen(true);
              // Rescore rewrites the snapshot with the AI's latest text;
              // read it fresh so the diff is against what the AI says now.
              api.getStage1Snapshot(matterId)
                .then((res) => setSnapshot(res.snapshot))
                .catch(() => {});
            }}
            className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-2.5 py-1 text-xs font-medium transition-colors flex items-center gap-1.5"
          >
            <GitCompare size={12} /> Diff vs AI
          </button>
          <button
            type="button"
            data-testid="summary-reextract-button"
            onClick={handleReextract}
            disabled={reExtracting || isFrozen}
            className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-2.5 py-1 text-xs font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50"
          >
            {reExtracting
              ? <Loader2 size={12} className="animate-spin" />
              : <RefreshCcw size={12} />}
            Re-extract
          </button>
        </div>
      </div>

      {/* ── Four text field cards ──────────────────────────────── */}
      {TEXT_FIELDS.map(({ apiField, contentKey, label, accent }) => (
        <TextFieldCard
          key={apiField}
          matterId={matterId}
          apiField={apiField}
          label={label}
          value={typeof summary?.[contentKey] === 'string' ? summary[contentKey] : ''}
          isEdited={editedFields.includes(apiField)}
          isFrozen={isFrozen}
          accent={!!accent}
          regenerating={regenField === apiField}
          onRegenerate={() => handleRegenerate(apiField)}
          onRefresh={onRefresh}
          onError={onError}
          suggestion={typeof suggestions[apiField] === 'string' ? (suggestions[apiField] as string) : null}
          onAcceptSuggestion={handleAcceptSuggestion}
          onDismissSuggestion={handleDismissSuggestion}
        />
      ))}

      {/* ── Prior Art card — chip styling preserved; add/remove
          wired through the same PATCH endpoint as the text fields. */}
      <PriorArtCard
        matterId={matterId}
        priorArt={Array.isArray(summary?.prior_art_mentioned) ? summary.prior_art_mentioned : []}
        isEdited={editedFields.includes('prior_art')}
        isFrozen={isFrozen}
        onRefresh={onRefresh}
        onError={onError}
      />

      {/* ── Footer ─────────────────────────────────────────────── */}
      <div
        data-testid="summary-edited-footer"
        className="text-[11px] text-zinc-400 pt-2 text-right"
      >
        {editedFields.length} of 5 fields edited from AI defaults
      </div>

      {/* ── Diff-vs-AI modal ───────────────────────────────────── */}
      <DiffModal
        isOpen={isDiffOpen}
        onClose={() => setIsDiffOpen(false)}
        currentFields={currentFieldsForDiff}
        snapshot={snapshot}
        isFrozen={isFrozen}
        onRevert={handleRevert}
      />
    </div>
  );
}

interface TextFieldCardProps {
  matterId: string;
  apiField: Exclude<Stage1SummaryField, 'prior_art'>;
  label: string;
  value: string;
  isEdited: boolean;
  isFrozen: boolean;
  /** Visual lift for the Overall Invention card — matches the blue
   *  treatment the screenshot uses for the top-of-summary field. */
  accent: boolean;
  regenerating: boolean;
  onRegenerate: () => void;
  onRefresh: () => Promise<void> | void;
  onError: (msg: string | null) => void;
  /** Fresh AI suggestion for this field, if the drafter clicked
   *  Regenerate. Null when no suggestion is pending. Rendered below
   *  the current content with Accept / Dismiss actions; does NOT
   *  auto-replace the field. */
  suggestion: string | null;
  onAcceptSuggestion: (f: Exclude<Stage1SummaryField, 'prior_art'>, v: string) => Promise<void> | void;
  onDismissSuggestion: (f: Exclude<Stage1SummaryField, 'prior_art'>) => void;
}

/** Single editable card. Click-to-edit: shows static text until the
 *  drafter clicks into the card body, at which point an auto-focused
 *  textarea replaces it. Blur persists via PATCH and flips the view
 *  back to static. */
function TextFieldCard({
  matterId, apiField, label, value, isEdited, isFrozen, accent,
  regenerating, onRegenerate, onRefresh, onError,
  suggestion, onAcceptSuggestion, onDismissSuggestion,
}: TextFieldCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const lastSavedRef = useRef(value);

  // Sync the draft when the parent re-renders with a new value (e.g.
  // after a Re-extract refetches the matter). Only if we're not
  // mid-edit — otherwise a poll-induced refresh would blow away the
  // drafter's unsaved changes.
  useEffect(() => {
    if (!editing) {
      setDraft(value);
      lastSavedRef.current = value;
    }
  }, [value, editing]);

  const handleBlur = async () => {
    setEditing(false);
    const next = draft;
    if (next === lastSavedRef.current) return;
    setSaving(true);
    onError(null);
    try {
      await api.patchStage1Summary(matterId, apiField, next);
      lastSavedRef.current = next;
      await onRefresh();
    } catch (e: any) {
      onError(apiErrorText(e, `Save failed (${apiField})`));
      // Roll back to last saved value so the UI never shows a value
      // the server doesn't know about.
      setDraft(lastSavedRef.current);
    } finally {
      setSaving(false);
    }
  };

  const base = 'border rounded-sm p-4 transition-colors ' + (
    accent ? 'bg-blue-50/40 border-blue-100' : 'bg-white border-zinc-200'
  );

  return (
    <div data-testid={`summary-field-card-${apiField}`} className={base}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className={
            'text-sm font-medium truncate ' +
            (accent ? 'text-[#002FA7]' : 'text-zinc-900')
          }>
            {label}
          </h4>
          {isEdited && (
            <span
              data-testid={`summary-field-edited-${apiField}`}
              className="inline-flex items-center px-1.5 py-0 rounded-sm text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200"
            >
              Edited
            </span>
          )}
          {saving && (
            <Loader2 size={11} className="animate-spin text-zinc-400" />
          )}
        </div>
        <button
          type="button"
          data-testid={`summary-field-regenerate-${apiField}`}
          onClick={onRegenerate}
          disabled={isFrozen || regenerating}
          className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-[#002FA7] disabled:opacity-50 inline-flex items-center gap-1 flex-shrink-0"
          title="Regenerate this field from the IDF"
        >
          {regenerating
            ? <Loader2 size={10} className="animate-spin" />
            : <RefreshCcw size={10} />}
          Regenerate
        </button>
      </div>

      {editing && !isFrozen ? (
        <textarea
          autoFocus
          data-testid={`summary-field-textarea-${apiField}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleBlur}
          rows={Math.max(3, Math.min(10, Math.ceil(draft.length / 80)))}
          className="w-full bg-white border border-[#002FA7] rounded-sm px-2 py-1.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y"
        />
      ) : (
        <div
          data-testid={`summary-field-content-${apiField}`}
          role={isFrozen ? undefined : 'button'}
          tabIndex={isFrozen ? -1 : 0}
          onClick={() => !isFrozen && setEditing(true)}
          onKeyDown={(e) => {
            if (isFrozen) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setEditing(true);
            }
          }}
          className={
            'text-sm leading-relaxed whitespace-pre-wrap rounded-sm -mx-1 px-1 ' +
            (isFrozen
              ? 'text-zinc-700 cursor-default'
              : 'text-zinc-800 cursor-text hover:bg-zinc-50')
          }
        >
          {value || (
            <span className="text-zinc-400 italic">
              No {label.toLowerCase()} extracted yet — run Analyze IDF (left panel), or click to write one.
            </span>
          )}
        </div>
      )}

      {/* Suggestion panel — only renders when the drafter has just
          clicked Regenerate. Never auto-replaces the field; Accept
          explicitly PATCHes the new value, Dismiss simply tears down
          the panel. */}
      {suggestion !== null && !isFrozen && (
        <div
          data-testid={`summary-field-suggestion-${apiField}`}
          className="mt-3 border border-blue-200 bg-blue-50/40 rounded-sm p-3"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#002FA7]">
              AI Suggestion
              <span className="ml-2 normal-case tracking-normal text-zinc-500">
                a new proposal — Accept replaces the text above; Dismiss keeps it
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                data-testid={`summary-field-suggestion-accept-${apiField}`}
                onClick={() => onAcceptSuggestion(apiField, suggestion)}
                className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-2.5 py-1 text-[11px] font-medium transition-colors"
              >
                Accept
              </button>
              <button
                type="button"
                data-testid={`summary-field-suggestion-dismiss-${apiField}`}
                onClick={() => onDismissSuggestion(apiField)}
                className="bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-2.5 py-1 text-[11px] font-medium transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
          <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">
            {suggestion || <span className="text-zinc-400 italic">empty</span>}
          </p>
        </div>
      )}
    </div>
  );
}

interface PriorArtCardProps {
  matterId: string;
  priorArt: string[];
  isEdited: boolean;
  isFrozen: boolean;
  onRefresh: () => Promise<void> | void;
  onError: (msg: string | null) => void;
}

/** Prior Art card. Chip pill styling matches the original inline
 *  Summary block (zinc-100/200 background, same typography). Each
 *  chip has a × that removes the entry; a [+ Add reference] button
 *  reveals an inline input. Both add and remove call the existing
 *  PATCH /api/matters/:id/stage1/summary endpoint with the new full
 *  array — the backend then maintains idf_edited_fields the same
 *  way it does for text fields. */
function PriorArtCard({
  matterId, priorArt, isEdited, isFrozen, onRefresh, onError,
}: PriorArtCardProps) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async (next: string[]) => {
    setBusy(true);
    onError(null);
    try {
      await api.patchStage1Summary(matterId, 'prior_art', next);
      await onRefresh();
    } catch (e: any) {
      onError(apiErrorText(e, 'Prior art save failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = (idx: number) => {
    if (isFrozen) return;
    const next = priorArt.filter((_, i) => i !== idx);
    void save(next);
  };

  const handleAdd = () => {
    const v = draft.trim();
    if (!v) return;
    if (priorArt.includes(v)) {
      // Idempotent: a duplicate add is a no-op rather than an error
      // — drafters retyping a citation by mistake shouldn't see a
      // failure modal.
      setDraft('');
      setAdding(false);
      return;
    }
    void save([...priorArt, v]).then(() => {
      setDraft('');
      setAdding(false);
    });
  };

  return (
    <div
      data-testid="summary-field-card-prior_art"
      className="border border-zinc-200 rounded-sm bg-white p-4"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-zinc-900">Prior Art</h4>
          {isEdited && (
            <span
              data-testid="summary-field-edited-prior_art"
              className="inline-flex items-center px-1.5 py-0 rounded-sm text-[9px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200"
            >
              Edited
            </span>
          )}
          {busy && <Loader2 size={11} className="animate-spin text-zinc-400" />}
        </div>
        {!isFrozen && !adding && (
          <button
            type="button"
            data-testid="prior-art-add-button"
            onClick={() => setAdding(true)}
            className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1"
          >
            + Add reference
          </button>
        )}
      </div>

      {priorArt.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {priorArt.map((a, i) => (
            <span
              key={i}
              data-testid={`prior-art-chip-${i}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-xs text-zinc-700"
            >
              {a}
              {!isFrozen && (
                <button
                  type="button"
                  data-testid={`prior-art-remove-${i}`}
                  onClick={() => handleRemove(i)}
                  disabled={busy}
                  className="text-zinc-400 hover:text-red-600 disabled:opacity-50"
                  title={`Remove ${a}`}
                  aria-label={`Remove ${a}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-sm text-zinc-400 italic">
          No prior art references extracted.
        </div>
      )}

      {adding && !isFrozen && (
        <div className="mt-3 flex items-center gap-1.5">
          <input
            autoFocus
            data-testid="prior-art-add-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }
              if (e.key === 'Escape') { setAdding(false); setDraft(''); }
            }}
            placeholder="e.g. EP3124351A1"
            className="flex-1 bg-white border border-[#002FA7] rounded-sm px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          />
          <button
            type="button"
            data-testid="prior-art-add-confirm"
            onClick={handleAdd}
            disabled={!draft.trim() || busy}
            className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40"
          >
            Add
          </button>
          <button
            type="button"
            data-testid="prior-art-add-cancel"
            onClick={() => { setAdding(false); setDraft(''); }}
            className="text-[11px] text-zinc-500 hover:text-zinc-900 px-1"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
